// 우측 정보 패널 (side-stack) — 바닐라 renderTargets/renderTraceGroups 이식 (Phase 3-B).
// 평가 대상 카드(전체/기업 필터) + 진행 내역(작업 단위 접이식 그룹 + 서브스텝 트리).
// traceGroups/targets 데이터 형식은 스냅샷 계약과 동일 — 변경 금지.
import { Fragment, useState } from "react";
import {
  cleanCompanyName,
  failedPhrase,
  finishedPhrase,
  type VaTarget,
} from "../../lib/chatText";
import { toolLabel } from "../../lib/toolLabels";
import { CompanyLogo } from "../../ui/CompanyLogo";
import type { TraceGroup, TraceRow } from "../../stores/chat";

interface Props {
  targets: VaTarget[];
  /** -1 = 전체(기본) */
  activeTargetIdx: number;
  onPickTarget: (idx: number) => void;
  traceGroups: TraceGroup[];
}

const OK_ICON = "✓", FAIL_ICON = "✕", WARN_ICON = "⚠";
const CHEV = (
  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const SEARCH = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

// 그룹 상태 — 하위 실행이 전부 성공이면 ✓, 전부 실패면 ✕, 섞였으면 ⚠
function groupStatus(g: TraceGroup): "ok" | "fail" | "warn" {
  const st = g.rows.map((r) => r.status).filter((x) => x === "ok" || x === "fail");
  if (!st.length) return g.ok ? "ok" : "fail";
  if (st.every((x) => x === "ok")) return "ok";
  if (st.every((x) => x === "fail")) return "fail";
  return "warn";
}

function RowLine({ r }: { r: TraceRow }) {
  // 실패한 단계를 "완료"로 표기하지 않는다
  const txt =
    r.status === "run" ? r.t : r.status === "fail" ? failedPhrase(r.t) : finishedPhrase(r.t);
  if (!txt) return null;
  const mark =
    r.status === "ok" ? (
      <i className="trace-sub-icon ok">{OK_ICON}</i>
    ) : r.status === "fail" ? (
      <i className="trace-sub-icon fail">{FAIL_ICON}</i>
    ) : r.status === "warn" ? (
      <i className="trace-sub-icon warn">{WARN_ICON}</i>
    ) : null;
  return (
    <div className={r.status === "run" ? "trace-sub-live" : undefined}>
      {txt}
      {mark}
    </div>
  );
}

export default function InfoPanel({
  targets,
  activeTargetIdx,
  onPickTarget,
  traceGroups,
}: Props) {
  // 단계(작업) 펼침 상태 — 렌더 단위가 작업이므로 작업명 기준으로 기억한다
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const activeKey =
    activeTargetIdx === -1
      ? null
      : targets[activeTargetIdx]
        ? targets[activeTargetIdx].ticker || targets[activeTargetIdx].name
        : null;
  const targetName = (sym: string | null): string => {
    const t = targets.find((x) => x.ticker === sym || x.name === sym);
    return t ? t.name : String(sym || "");
  };

  // 시안(Sources Panel ③) 구조: 단계(작업)가 상위, 기업이 하위 행.
  // '전체'(기본)는 모든 기업을 한 흐름으로, 기업을 고르면 그 기업 것만 필터.
  const shown = traceGroups.filter((g) => !activeKey || !g.symbol || g.symbol === activeKey);
  const byTool: Array<{ tool: string; members: TraceGroup[] }> = [];
  const idx = new Map<string, number>();
  shown.forEach((g) => {
    const k = g.tool;
    if (!idx.has(k)) {
      idx.set(k, byTool.length);
      byTool.push({ tool: k, members: [] });
    }
    byTool[idx.get(k) as number].members.push(g);
  });

  return (
    <aside className="side-stack">
      <section className="info-card" id="targetCard" hidden={targets.length === 0}>
        <div className="info-kicker">
          평가 대상 <span className="target-count" />
        </div>
        <div className="target-list">
          {/* '전체'가 기본 — 기업이 둘 이상일 때만 의미가 있다. 기업명을 누르면 필터. */}
          {targets.length >= 2 && (
            <button
              className={`target-row${activeTargetIdx === -1 ? " active" : ""}`}
              type="button"
              onClick={() => onPickTarget(-1)}
            >
              <span className="info-title">전체</span>
            </button>
          )}
          {targets.map((t, i) => (
            <button
              key={`${t.ticker || t.name}-${i}`}
              className={`target-row${i === activeTargetIdx ? " active" : ""}`}
              type="button"
              onClick={() => onPickTarget(i)}
            >
              <span className="target-name">
                <CompanyLogo symbol={t.ticker} name={t.name} size="sm" />
                <span className="info-title">{t.name}</span>
              </span>
              <span className="target-ticker">{t.ticker || ""}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="info-card">
        <div className="info-kicker">진행 내역</div>
        <div className="trace-title">
          {byTool.length ? `${byTool.length}개 작업 진행.` : "아직 진행한 작업이 없습니다."}
        </div>
        <ul className="kv trace">
          {byTool.length === 0 && (
            <li>
              <span>Status</span>
              <span>idle</span>
            </li>
          )}
          {byTool.map((grp) => {
            const done = grp.members.every((g) => g.done);
            const sts = grp.members.map(groupStatus);
            const st = sts.every((x) => x === "ok")
              ? "ok"
              : sts.every((x) => x === "fail")
                ? "fail"
                : "warn";
            const isOpen = !!open[grp.tool];
            // 전체 뷰에서 기업이 둘 이상 걸린 작업이면 기업명 소제목으로 나눠 보여준다
            const multi = !activeKey && grp.members.filter((g) => g.symbol).length > 1;
            const hasRows = grp.members.some((g) =>
              g.rows.some((r) =>
                r.status === "run" ? r.t : r.status === "fail" ? failedPhrase(r.t) : finishedPhrase(r.t),
              ),
            );
            return (
              <li
                key={grp.tool}
                className={`trace-group${isOpen ? " open" : ""}`}
                data-tool={grp.tool}
              >
                <button
                  className="trace-head"
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [grp.tool]: !o[grp.tool] }))}
                >
                  {CHEV}
                  {toolLabel(grp.tool)}
                  {done ? (
                    <span className={`trace-state ${st}`}>
                      {st === "ok" ? OK_ICON : st === "fail" ? FAIL_ICON : WARN_ICON}
                    </span>
                  ) : (
                    <span className="trace-state">{SEARCH}</span>
                  )}
                </button>
                {isOpen && hasRows && (
                  <div className="trace-rows">
                    {grp.members.map((g, mi) => {
                      const rows = g.rows.map((r, ri) => <RowLine r={r} key={ri} />);
                      if (!g.rows.length) return null;
                      return multi && g.symbol ? (
                        <Fragment key={mi}>
                          <div className="trace-co">
                            {cleanCompanyName(targetName(g.symbol))}
                          </div>
                          {rows}
                        </Fragment>
                      ) : (
                        <Fragment key={mi}>{rows}</Fragment>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </aside>
  );
}
